import {
  RAIL_FADE_REM,
  STOREFRONT_ARROW_REM,
  STOREFRONT_ROUNDED_PX,
  STOREFRONT_TITLE_REM,
  type StorefrontTitleCard,
} from "@/domain/storefront-appearance";
import { Button, Heading, Icon, pageContainerVariants, Surface, type TextTone } from "@/components/ui";
import { cn } from "@/lib/cn";
import { useStorefrontAppearance } from "@/hooks/use-storefront-appearance";
import { Back, Chevron } from "@/icons";
import type { ReactNode, Ref } from "react";

/**
 * Storefront column: the `PageContainer` gutter (90%) capped at the shell width, so rails,
 * grids and the store header share one edge. Callers must not invent their own gutter.
 */
export const RAIL_COLUMN = cn(pageContainerVariants({ gutter: "container" }), "max-w-shell");

/**
 * Soft rail edge mask (`softArea`). `left`/`right` mean that end still has
 * content to scroll toward. A finished end stays SHARP — fading the first card
 * with nothing before it would erase content for no reason.
 */
interface EdgeFadeMaskProps {
  left: boolean;
  right: boolean;
}
export function edgeFadeMask({ left, right }: EdgeFadeMaskProps): string {
  const fade = `${RAIL_FADE_REM}rem`;
  const start = left ? `transparent 0, black ${fade}` : "black 0, black 0";
  const end = right ? `black calc(100% - ${fade}), transparent 100%` : "black 100%, black 100%";
  return `linear-gradient(to right, ${start}, ${end})`;
}

/**
 * Optional title BAND behind a block heading. Off returns raw children — no
 * extra div — so toggling does not shift margin. Shared with menu category
 * names so padding cannot drift. Paints with `bg-title-card`, the band's own
 * token (follows card until the merchant pins a color).
 */
interface TitleBandProps {
  titleCard: StorefrontTitleCard;
  children: ReactNode;
}
export function TitleBand({ titleCard, children }: TitleBandProps) {
  if (!titleCard.visible) return <>{children}</>;

  return (
    <div
      data-testid="storefront-title-band"
      className="bg-title-card px-4 py-2.5 shadow-card"
      style={{ borderRadius: `${STOREFRONT_ROUNDED_PX[titleCard.rounded]}px` }}
    >
      {children}
    </div>
  );
}

/**
 * Title tone that changes with the band: on-band uses the title-card text role;
 * off-band follows the page surface. Wrong choice on a dark theme erases the title.
 */
export function titleTone(titleCard: boolean): TextTone {
  return titleCard ? "on-title-card" : "default";
}

/**
 * Shell for any home rail: background, title, and column. SCROLLER stays
 * outside on purpose — coupon loop vs item row differ. Reads `titleSize`
 * itself (page rhythm); BAND comes via prop (per-block weight).
 */
interface SectionRailProps {
  label: string;
  titleCard: StorefrontTitleCard;
  children: ReactNode;
  sectionRef?: Ref<HTMLElement>;
  /** Use the parent column when embedded in a container such as the cart. */
  columnClassName?: string;
}
export function SectionRail({
  label,
  titleCard,
  children,
  sectionRef,
  columnClassName = RAIL_COLUMN,
}: SectionRailProps) {
  const { titleSize } = useStorefrontAppearance();

  return (
    <Surface surface="page" as="section" ref={sectionRef} aria-label={label} className="pt-4 pb-2">
      <div className={`${columnClassName} mb-3`}>
        <TitleBand titleCard={titleCard}>
          <Heading
            clamp={1}
            tone={titleTone(titleCard.visible)}
            title={label}
            style={{ fontSize: `${STOREFRONT_TITLE_REM[titleSize]}rem` }}
          >
            {label}
          </Heading>
        </TitleBand>
      </div>
      {children}
    </Surface>
  );
}

/** Chevron glyph at medium `arrowSize`, in rem; other sizes scale from this anchor. */
const ARROW_GLYPH_AT_MEDIUM_REM = 1.125;

/**
 * Rail arrow. Outside the mask on purpose — inside it would sit in the fade.
 * Follows the palette (`cardBackground` / `cardText`); a fixed white chip
 * glows on dark themes. Shadow separates arrow from the art, not color.
 */

interface RailArrow {
  direction: "prev" | "next";
  onClick: () => void;
}

export function RailArrow({ direction, onClick }: RailArrow) {
  const { arrowSize } = useStorefrontAppearance();
  const diameterRem = STOREFRONT_ARROW_REM[arrowSize];
  const glyph = ARROW_GLYPH_AT_MEDIUM_REM * (diameterRem / STOREFRONT_ARROW_REM.medium);

  return (
    <Button
      variant="elevated"
      tone="neutral"
      size="auto"
      shape="circle"
      aria-label={direction === "prev" ? "Anterior" : "Próximo"}
      onClick={onClick}
      style={{ width: `${diameterRem}rem`, height: `${diameterRem}rem` }}
      className={cn("absolute top-1/2 -translate-y-1/2", direction === "prev" ? "left-3" : "right-3")}
    >
      <Icon as={direction === "prev" ? Back : Chevron} size="custom" customSize={glyph} weight="regular" />
    </Button>
  );
}
